
import { FormPage } from '@/mixins'
import { Component, Mixins, Prop } from 'vue-property-decorator'
import StateEditor from '@/components/collections/zones/StateEditor.vue'
import AdditionalZoneEditor from '@/components/collections/business/AdditionalZoneEditor.vue'
import { ZoneModel } from 'tradingmate-modules/src/models/api/admin'
import {
  BusinessBranchModel,
  BusinessBranchSocialLink,
  SocialLinkType
} from 'tradingmate-modules/src/models/api/businesses'
import { Services } from 'tradingmate-modules/src/services'
import SignupCheckoutRequest, { SignupPackage } from 'tradingmate-modules/src/models/api/signup/SignupCheckoutRequest'
import AvailableCouponsRequest from 'tradingmate-modules/src/models/api/payment/AvailableCouponsRequest'
import AvailableCouponsResponse from 'tradingmate-modules/src/models/api/payment/AvailableCouponsResponse'
import WebsiteDomainChecker from '@/components/collections/my-website/WebsiteDomainChecker.vue'
import BusinessBranchMembershipType
  from 'tradingmate-modules/src/models/api/businesses/branches/BusinessBranchMembershipType'
import Utils from 'tradingmate-modules/src/Utils'
import CheckoutModal from './CheckoutModal.vue'
import PackageCheckoutQueries from '../../../../../tradingmate-modules/src/models/common/PackageCheckoutQueries'
import { UserModel } from 'tradingmate-modules/src/models/api/users'

export class Coupon {
  id: string;
  name: string;
  amountOff: string;
  percentOff: string;
  description: string;

  constructor (data: Partial<Coupon>) {
    this.id = data.id ?? ''
    this.name = data.name ?? ''
    this.amountOff = data.amountOff ?? ''
    this.percentOff = data.percentOff ?? ''
    this.description = data.description ?? ''
  }
}

@Component({
  components: {
    WebsiteDomainChecker,
    AdditionalZoneEditor,
    StateEditor,
    CheckoutModal
  }
})
export default class PackageCheckout extends Mixins(FormPage) {
  private utils = Utils;

  // State & region
  private homeState = ''
  private homeRegion = ''
  private wantsAdditionalStateOrRegion: boolean | null = null
  private statewideSelected = false
  private additionalZones: ZoneModel[] = []
  private mainStateId = ''

  // Website
  private hasWebsiteLink = false
  private websiteDomainValid = false
  private websiteDomain = ''
  private lastWebsiteDomainSearched = ''
  private websiteSelected: boolean | null = null

  // Promo
  private photographSelected: boolean | null = false
  private promoPackSelected: boolean | null = false
  // private allAussieExtrasSelected: boolean | null = null
  private nationalSelected: boolean | null = false
  // private additionalStates: DropdownListItem[] = []

  // Display Toggles
  private showRegionDropdown = false
  private showPromoDropdown = false
  private showAussieDropdown = false
  private seeMore = false
  private displayAddStateOrRegions = false;
  private displayWebsite = false;
  private displayPackages = false;

  // Pre-filled stuff
  private wantsExpress = false
  private expressSelected = false

  // Webmaster Only
  private isWebmasterCheckout = false;
  private isActivateManualLoading = false;
  private isActivateFreeLoading = false;
  private isSubsciptionLoading = false;
  private isSendInvoiceToEmailLoading = false;
  private isGenerateReceiptLoading = false;

  private hasSubscription = false

  private selectedCoupon = ''
  private availableCoupons: Coupon[] = []

  private notification = ''
  private showNotification = false

  currentModal: string | null = null

  @Prop({ required: true })
  private readonly business!: BusinessBranchModel

  @Prop({ default: null })
  private readonly user!: UserModel | null

  mounted (): void {
    // Check if this checkout is being done by the webmaster
    Services.API.Users.Me()
      .then(
        (response) => {
          if (response != null) this.isWebmasterCheckout = response.IsWebmaster
          if (this.isWebmasterCheckout) { this.getAvailableCoupons() }
        })
      .catch((errors) => {
        console.error(errors)
      })

    this.getHasSubscription()

    this.homeState = Utils.getState(this.business.StateId)?.Name ?? ''
    this.homeRegion = this.business.MainZone.TMName

    // Check if business already has a website link
    const socialLinks: BusinessBranchSocialLink[] = this.business.SocialLinks
    const websiteLink = socialLinks.some(x => x.LinkType === SocialLinkType.Website)
    if (websiteLink) {
      this.hasWebsiteLink = true
      this.websiteSelected = false
    }

    Services.API.MyWebsites.GetMyWebsite(this.business.BusinessBranchId)
      .then((response) => {
        if (response.Domain != null) this.hasWebsiteLink = true
      })
      .catch((errors) => {
        console.error(errors)
      })

    this.photographSelected = !!this.$route.query.photo

    this.wantsExpress = !!this.$route.query.express
    this.expressSelected = this.wantsExpress

    const packagePreSelection: PackageCheckoutQueries = this.$route.query.p as PackageCheckoutQueries

    switch (packagePreSelection) {
      case PackageCheckoutQueries.B2b: {
        this.onLocalClick()
        break
      }
      case PackageCheckoutQueries.StateWide: {
        if (this.business.StateId) { this.stateSelected(this.business.StateId.toString()) }
        break
      }
      case PackageCheckoutQueries.Website: {
        if (this.business.StateId) { this.stateSelected(this.business.StateId.toString()) }
        this.suggestDomainName()
        this.onWebsiteSelected()
        break
      }
      case PackageCheckoutQueries.Promo: {
        if (this.business.StateId) { this.stateSelected(this.business.StateId.toString()) }
        this.suggestDomainName()
        this.onPromoPackClick()
        break
      }
      case PackageCheckoutQueries.AllAussieExtra: {
        this.suggestDomainName()
        this.onNationalClickYes()
        break
      }
    }

    // if they have already selected a package then select that package.
    // automatically select state if there is state package and then
  }

  suggestDomainName (): void {
    this.websiteDomain = this.business.TradingName.replace(' ', '')
  }

  negativeOptionScheme (state: boolean | null): 'primary' | 'subtle' | 'tertiary' {
    if (state === null) {
      return 'subtle'
    }
    return state ? 'subtle' : 'tertiary'
  }

  // PRICING CALCULATIONS
  get subscriptionPrice (): number {
    if (this.websiteSelected || this.promoPackSelected || this.nationalSelected)
    {
      // No charge for these packages
      return 0
    }

    // if (this.business.NonProfitCharity)
    // {
    //   return 49.5
    // }

    return 99
  }

  get expressPricing (): number {
    if (!this.expressSelected) return 0
    return 66
  }

  get regionPricing (): number {
    let runningTotal = 0

    if (this.promoPackSelected || this.nationalSelected)
    {
      return 0
    }

    if (this.localSelected || this.statewideSelected || this.websiteSelected) {
      runningTotal += 99
    }

    if (this.mainStateId)
    {
      const price = this.utils.getState(this.mainStateId.toString())?.Price ?? 0
      runningTotal += price
    }

    runningTotal += (this.additionalZones.length * 22)
    return runningTotal
  }

  get isAdditionalZoneOrStateSelected (): boolean {
    console.log(this.additionalZones.length)
    if (this.mainStateId !== '' || this.additionalZones.length > 0) {
      return true
    }
    else {
      return false
    }
  }

  get isMembershipIncluded (): boolean {
    if (this.websiteSelected || this.promoPackSelected || this.nationalSelected) {
      return true
    }
    else {
      return false
    }
  }

  get isWebsiteIncluded (): boolean {
    if (this.promoPackSelected || this.nationalSelected) {
      return true
    }
    else {
      return false
    }
  }

  get photographPricing (): number {
    return this.photographSelected ? 220 : 0
  }

  get websitePrice (): number {
    if (this.nationalSelected || this.promoPackSelected) return 0
    if (this.websiteSelected) {
      return 825
    }
    return 0
  }

  get promoPackPrice (): number {
    if (!this.promoPackSelected) return 0
    if (this.mainStateId)
    {
      return 1320
    }
    return 1320
  }

  get allAussiesExtraPrice (): number {
    if (!this.nationalSelected) return 0
    return 2310
  }

  // What you will actually be paying
  get totalPrice (): number {
    let runningTotal = 0

    // Base membership
    runningTotal += this.subscriptionPrice

    // Express
    runningTotal += this.expressPricing

    // Zone/state
    if (this.statewideSelected && this.mainStateId && !this.promoPackSelected && !this.websiteSelected) {
      runningTotal += this.utils.getState(this.mainStateId.toString())?.Price ?? 0
    }

    // Extra regions
    runningTotal += (this.additionalZones.length * 22)

    // Photo
    runningTotal += this.photographPricing

    // Website
    runningTotal += this.websitePrice

    // Promo pack
    if (this.promoPackSelected) { runningTotal += 1320 }

    // National/All aussie extras
    if (this.nationalSelected) { runningTotal += 2310 }

    // Apply coupon override
    if (this.selectedCoupon !== '') {
      var matchingCoupon = this.availableCoupons.find(coupon => coupon.id === this.selectedCoupon)

      if (matchingCoupon) {
        if (matchingCoupon?.amountOff !== '' && matchingCoupon?.amountOff !== null) {
          runningTotal -= Number(matchingCoupon.amountOff) / 100
        } else {
          runningTotal *= 1 - (Number(matchingCoupon.percentOff) / 100)
        }
      }
    }

    // Clamp
    runningTotal = Math.max(0, runningTotal)

    return runningTotal
  }

  // RAW VALUES
  get allAussieExtrasValue (): number {
    if (this.nationalSelected) return 3206
    return 0
  }

  get promoPackValue (): number {
    if (this.promoPackSelected) return 1628
    return 0
  }

  get isRenewing (): boolean {
    // Don't use renewal calculatation if coupon override is applied
    if (this.selectedCoupon !== '') {
      return false
    }

    // Only display renewal discount if service selected is not changing
    switch (this.business.MembershipType) {
      case BusinessBranchMembershipType.B2B:
        if (this.localSelected) { return true }
        break
      case BusinessBranchMembershipType.Statewide:
        if (this.statewideSelected) { return true }
        break
      case BusinessBranchMembershipType.Website:
        if (this.websiteSelected) { return true }
        break
      case BusinessBranchMembershipType.PromoPack:
        return false
      case BusinessBranchMembershipType.National:
        return false
      default:
        return false
    }

    return false
  }

  get totalValue (): number {
    let runningTotal = 0
    runningTotal += this.allAussieExtrasValue
    runningTotal += this.promoPackValue
    runningTotal += this.photographPricing
    runningTotal += this.websitePrice
    runningTotal += (this.additionalZones.length * 22)
    if (this.mainStateId)
    {
      const state = this.utils.getState(this.mainStateId.toString())
      runningTotal += state?.Price ?? 0
    }
    return runningTotal
  }

  get savings (): number {
    return this.totalValue - this.totalPrice
  }

  // COMPUTED HELPERS
  get localSelected (): boolean {
    return !(this.statewideSelected || this.websiteSelected || this.nationalSelected)
  }

  get renewalPrice (): number {
    const expiryDate = this.business.DateExpiration
    const cleaned = expiryDate.replace(/[{}]/g, '').trim()
    const date = new Date(cleaned)

    const graceExpiry = new Date(date)
    graceExpiry.setUTCDate(graceExpiry.getUTCDate() + 30)

    const currentDate = new Date()

    const useGracePeriodDevFlag = false // To be enabled after July, later removed after live testing

    if (useGracePeriodDevFlag && graceExpiry < currentDate) {
      // Grace period has passed

      if (this.business.NonProfitCharity) {
        return this.totalPrice - 59.25
      } else {
        return this.totalPrice - 19.50
      }
    } else {
      if (this.business.NonProfitCharity) {
        return this.totalPrice / 4
      } else {
        return this.totalPrice / 2
      }
    }
  }

  get affirmationText (): string {
    if (
      this.photographSelected === null ||
      this.websiteSelected === null ||
      this.promoPackSelected === null ||
      this.nationalSelected === null
    ) { return 'Continue selecting package options to checkout' }
    if ((this.promoPackSelected && this.websiteSelected) || (this.nationalSelected && this.websiteSelected))
    {
      return 'CONGRATULATIONS!' + '\n' + 'YOU JUST RECEIVED OUR BEST DEAL EVER!!'
    }
    if (this.nationalSelected)
    {
      return 'CONGRATULATIONS! THIS IS A TERRIFIC SAVING ON BIZ ADVERTISING!!'
    }
    if (this.promoPackSelected)
    {
      return 'CONGRATULATIONS! THIS IS A TERRIFIC SAVING ON BIZ ADVERTISING!!'
    }
    return 'CONGRATULATIONS & WELCOME TO TRADING MATE!!'
  }

  private errorMessage = ''

  // INPUT HANDLERS
  onMainStateInput (stateId: string): void {
    if (!stateId) return
    this.mainStateId = stateId
    this.onStatewideClick()
  }

  onLocalClick (): void {
    this.errorMessage = ''
    // this.localSelected = true
    this.statewideSelected = false
    this.nationalSelected = false
    // this.additionalStates = []
    this.additionalZones = []
    // this.allAussieExtrasSelected = false
    this.promoPackSelected = false
  }

  onWebsiteSelected (): void {
    this.errorMessage = ''
    if (!(this.statewideSelected || this.nationalSelected))
    {
      this.onStatewideClick()
    }
    this.websiteSelected = true
  }

  handleDomainAvailability (val: boolean): void {
    this.websiteDomainValid = val
    this.lastWebsiteDomainSearched = this.websiteDomain
  }

  stateSelected (stateId: string): void {
    this.errorMessage = ''

    // Handle national selection
    if (stateId === 'National') {
      this.onNationalClickYes()

      return
    }

    // Handle individual state selection
    const state = this.utils.getState(stateId)
    if (state) {
      this.mainStateId = stateId
      this.onStatewideClick()
    } else {
      this.statewideSelected = false
      this.nationalSelected = false
    }
  }

  onStatewideClick (): void {
    this.errorMessage = ''
    this.statewideSelected = true
    this.nationalSelected = false
  }

  onRegionDropdown (): void {
    this.showRegionDropdown = !this.showRegionDropdown
  }

  onPromoDropdown (): void {
    this.showPromoDropdown = !this.showPromoDropdown
  }

  onAussieDropdown (): void {
    this.showAussieDropdown = !this.showAussieDropdown
  }

  onPromoPackClick (): void {
    this.errorMessage = ''
    this.promoPackSelected = true
    this.onStatewideClick()
  }

  onNationalClickYes (): void {
    this.errorMessage = ''

    console.log('national clicked')

    // Toggle
    if (this.nationalSelected === false)
    {
      console.log('toggle on')
      this.statewideSelected = false
      this.promoPackSelected = false
      this.additionalZones = []
      this.nationalSelected = true
      this.mainStateId = 'National'

      // Display flags
      this.displayAddStateOrRegions = true
      this.displayWebsite = true
      this.displayPackages = true
    } else
    {
      console.log('toggle off')
      this.mainStateId = ''
      this.nationalSelected = false
    }
  }

  onNationalClickNo (): void {
    this.errorMessage = ''

    console.log('national clicked')
    console.log('toggle off')
    this.mainStateId = ''
    this.nationalSelected = false
  }

  isCheckoutSelectionValid (): boolean {
    this.errorMessage = ''
    // Must have made a selection for every option
    console.log('go to checkout')

    if (
      this.websiteSelected === null ||
      this.photographSelected === null ||
      this.promoPackSelected === null ||
      this.nationalSelected === null
    ) {
      console.log('all options not selected')
      this.errorMessage = 'You must make a selection for every option'
      // this.affirmationText = 'Please select yes or no to all options'
      return false
    }

    // Must select a state in statewide
    if ((this.statewideSelected || this.websiteSelected || this.promoPackSelected) && this.mainStateId === '') {
      const stateEditor = this.$refs.stateEditor as StateEditor
      const modelState: Record<string, string[]> = { StateEditor: ['Must choose a state'] }
      stateEditor.httpError = { Status: 400, ModelState: modelState }
      console.log('cant be in a statewide package without having picking state')
      return false
    }

    // Must have a valid domain if wants website
    if (this.websiteSelected && !this.websiteDomainValid) {
      const domainChecker = this.$refs.domainChecker as WebsiteDomainChecker
      const modelState: Record<string, string[]> = { Available: ['Must choose domain for website'] }
      domainChecker.httpError = { Status: 400, ModelState: modelState }
      console.log('If you want a website you must pass the domain check')
      return false
    }

    // All checks pass
    return true
  }

  onGoToCheckout (): void {
    if (!this.isCheckoutSelectionValid()) return

    // Selections are valid create checkout request to stripe
    this.loading = true

    const branchId: string = this.business.BusinessBranchId
    if (!branchId) return

    const request = this.getCheckoutRequest()
    if (!request) return

    console.log('request package: ' + request.SignupPackage.toString())
    console.log('coupon id: ' + request.OverrideCouponId)

    Services.API.Payment.CheckoutRequest(branchId, request)
      .then((response) => {
        console.log('response')
        console.info(response)
        window.open(response.SessionUrl, '-self')
      })
      .catch((error) => {
        this.httpError = error
        console.log(error)
        console.error(error)
      })
      .finally(() => {
        this.loading = false
      })
  }

  onGenerateInvoice (): void {
    if (!this.isCheckoutSelectionValid()) return

    this.loading = true
    const branchId: string = this.business.BusinessBranchId
    if (!branchId) return

    console.log(branchId)

    const request = this.getCheckoutRequest()
    if (!request) return

    this.loading = true
    Services.API.Invoices.CheckoutGenerateInvoice(branchId, request)
      .then((returnData) => {
        // Create a blob URL from the received blob
        const url = window.URL.createObjectURL(new Blob([returnData], { type: 'application/pdf' }))

        // Create a temporary anchor element
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `Invoice_${branchId}.pdf`) // set filename here
        document.body.appendChild(link)

        // Trigger the download
        link.click()

        // Clean up
        link.remove()
        window.URL.revokeObjectURL(url)
      })
      .catch((error) => {
        console.error(error)
      })
      .finally(() => { this.loading = false })
  }

  private payLaterLoading = false
  private payLaterResolved = false
  onPayLaterInvoice (): void {
    this.payLaterLoading = true
    this.payLaterResolved = false
    const branchId: string = this.business.BusinessBranchId
    if (!branchId) return
    const request = this.getCheckoutRequest()
    if (!request) return
    Services.API.Payment.PayLaterRequest(branchId, request)
      .then((response) => {
        this.payLaterResolved = response
      })
      .catch((errors) => {
        console.error(errors)
      })
      .finally(() => {
        this.payLaterLoading = false
      })
  }

  onAdditionalStateRegionButtonSelected (): void {
    this.errorMessage = ''
    this.displayAddStateOrRegions = true
    this.wantsAdditionalStateOrRegion = true
  }

  onWebsiteButtonSelected (): void {
    this.errorMessage = ''

    // Also display the dependant options for states/regions and packages
    this.displayAddStateOrRegions = true
    this.wantsAdditionalStateOrRegion = true
    this.displayPackages = true

    this.displayWebsite = true
    this.websiteSelected = true

    // Default additional state to match home state
    this.mainStateId = this.business.StateId
  }

  onRemoveState (id: string): void {
    const index = this.additionalZones.findIndex((item) => item.ZoneId === id)
    if (index === -1) return
    this.additionalZones.splice(index, 1)
  }

  getCheckoutRequest (): SignupCheckoutRequest | void {
    console.log('Is express?')
    console.log(this.expressSelected)
    console.log('Is local?')
    console.log(this.localSelected)
    console.log('Is statewide?')
    console.log(this.statewideSelected)

    const request = new SignupCheckoutRequest()
    if (this.selectedCoupon !== '') {
      request.OverrideCouponId = this.selectedCoupon
    }

    if (this.expressSelected) {
      request.ExpressSignup = true
    }

    if (this.additionalZones.length)
    {
      request.AdditionalZoneIds = this.additionalZones.map(x => x.ZoneId)
    }

    if (this.photographSelected) {
      request.WantsPhoto = true
    }

    if (this.localSelected) {
      request.SignupPackage = SignupPackage.B2b
      // cant have any more extras / options
      return request
    }

    if (this.statewideSelected) {
      request.SignupPackage = SignupPackage.Statewide
      request.MainStateId = this.mainStateId
    }

    if (this.websiteSelected) {
      request.SignupPackage = SignupPackage.Website
      request.WantsWebsite = true
      request.Domain = this.websiteDomain += '.au'
      request.MainStateId = this.mainStateId
    }

    // Will keep website details and override package
    if (this.promoPackSelected) {
      request.SignupPackage = SignupPackage.PromoPack
      request.MainStateId = this.mainStateId
    }

    // will override website package but keep other info
    if (this.nationalSelected) {
      request.SignupPackage = SignupPackage.AllAussieExtras
    }

    return request
  }

  // Webmaster Control Panel Stuff
  onActivateManual (): void {
    this.currentModal = null
    this.isActivateManualLoading = true

    var request = this.getCheckoutRequest()
    if (!request) return

    Services.API.Payment.CheckoutManualRequest(this.business.BusinessBranchId, request)
      .then((response) => {
        if (response) {
          this.notification = 'User was activated successfully'
          this.showNotification = true
        } else {
          this.notification = 'User activation failed - unknown error'
          this.showNotification = true
        }
      })
      .catch((errors) => {
        console.error(errors)
        this.notification = 'User activation failed - error occured'
        this.showNotification = true
      })
      .finally(() => {
        this.isActivateManualLoading = false
      })
  }

  onActivateFree (): void {
    this.currentModal = null
    this.isActivateFreeLoading = true

    var request = this.getCheckoutRequest()
    if (!request) return

    Services.API.Payment.CheckoutFreeRequest(this.business.BusinessBranchId, request)
      .then((response) => {
        if (response) {
          this.notification = 'User was activated successfully'
          this.showNotification = true
        } else {
          this.notification = 'User activation failed - unknown error'
          this.showNotification = true
        }
      })
      .catch((errors) => {
        console.error(errors)
        this.notification = 'User activation failed - error occured'
        this.showNotification = true
      })
      .finally(() => {
        this.isActivateFreeLoading = false
      })
  }

  onAddMonthlySubscription (): void {
    this.currentModal = null
    this.isSubsciptionLoading = true

    Services.API.Payment.AddSubscriptionManualPayment(this.business.BusinessBranchId)
      .then((response) => {
        this.notification = 'Subscription checkout session launched successfully'
        this.showNotification = true
        window.open(response.SessionUrl, '-self')
      })
      .catch((error) => {
        console.error(error)

        const message = error.response?.data?.error || 'Add subscription failed'

        this.notification = `${message}`
        this.showNotification = true
      })
      .finally(() => {
        this.isSubsciptionLoading = false
        this.getHasSubscription()
      })
  }

  onRemoveMonthlySubscription (): void {
    this.currentModal = null
    this.isSubsciptionLoading = true

    Services.API.Payment.RemoveSubscription(this.business.BusinessBranchId)
      .then((response) => {
        this.notification = 'Subscription has been removed'
        this.showNotification = true
      })
      .catch((error) => {
        console.error(error)
        const message = error.response?.data?.error || 'Remove subscription failed'

        this.notification = `${message}`
        this.showNotification = true
      })
      .finally(() => {
        this.isSubsciptionLoading = false
        this.getHasSubscription()
      })
  }

  onSendInvoiceToEmail (): void {
    this.currentModal = null

    console.log('onSendInvoiceToEmail')

    if (!this.isCheckoutSelectionValid()) return

    const branchId: string = this.business.BusinessBranchId
    if (!branchId) return

    const request = this.getCheckoutRequest()
    if (!request) return

    this.isSendInvoiceToEmailLoading = true
    this.loading = true

    Services.API.Invoices.CheckoutGenerateAndEmailInvoice(branchId, request)
      .then((returnData) => {
        console.log(returnData)
      })
      .catch((error) => {
        console.error(error)
      })
      .finally(() => {
        this.isSendInvoiceToEmailLoading = false
        this.loading = false
      })
  }

  onGenerateReceipt (): void {
    this.currentModal = null

    if (!this.isCheckoutSelectionValid()) return

    this.isGenerateReceiptLoading = true
    const branchId: string = this.business.BusinessBranchId
    if (!branchId) return

    const request = this.getCheckoutRequest()
    if (!request) return

    Services.API.Invoices.CheckoutGenerateReceipt(branchId, request)
      .then((returnData) => {
        // Create a blob URL from the received blob
        const url = window.URL.createObjectURL(new Blob([returnData], { type: 'application/pdf' }))

        // Create a temporary anchor element
        const link = document.createElement('a')
        link.href = url
        link.setAttribute('download', `Invoice_${branchId}.pdf`) // set filename here
        document.body.appendChild(link)

        // Trigger the download
        link.click()

        // Clean up
        link.remove()
        window.URL.revokeObjectURL(url)
      })
      .catch((error) => {
        console.error(error)
      })
      .finally(() => { this.isGenerateReceiptLoading = false })
  }

  getAvailableCoupons (): void {
    var request = new AvailableCouponsRequest()

    Services.API.Payment.GetAvailableCoupons(request)
      .then((response) => {
        this.availableCoupons = response.Coupons
        console.log(response)
      })
      .catch((errors) => {
        console.error(errors)
      })
      .finally(() => {
        this.payLaterLoading = false
      })
  }

  getHasSubscription (): void {
    this.loading = true

    Services.API.Payment.GetSubscriptionStatus(this.business.BusinessBranchId)
      .then((response) => {
        this.hasSubscription = response
      })
      .catch((errors) => {
        this.hasSubscription = false
      })
      .finally(() => {
        this.loading = false
      })
  }
}
